import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Form, message, Modal, Tooltip } from 'antd';
import {
  ElsaClientCompany,
  MinimalClientCompany,
} from '../types/ClientCompany';
import { ArrowRightOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import ClientCompanyAPI from '../api/ClientCompanyAPI';
import { isAxiosError } from 'axios';
import FieldSelect from '../../components/FieldSelect';
import ElsaAPI from '../api/ElsaAPI';
import { twMerge } from 'tailwind-merge';

type MapCompanyModalValues = {
  elsaClientCompanyId?: string;
};

type MapCompanyModalProps = {
  clientCompany: MinimalClientCompany;
  close: () => void;
  onUpdate?: (clientCompany: MinimalClientCompany) => void;
};

export const MapCompanyModal: FC<MapCompanyModalProps> = ({
  clientCompany,
  close,
  onUpdate,
}) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [createNewInElsa, setCreateNewInElsa] = useState(false);
  const elsaClientCompanyId = Form.useWatch('elsaClientCompanyId', form);

  const [elsaClientCompanies, setElsaClientCompanies] = useState<
    ElsaClientCompany[]
  >([]);
  const [loadingElsaClientCompanies, setLoadingElsaClientCompanies] =
    useState(false);
  const fetchElsaClientCompanies = useCallback(async () => {
    setLoadingElsaClientCompanies(true);
    try {
      const { data } = await ElsaAPI.getElsaClientCompanies(
        clientCompany.subsidiaryCompany.id,
      );
      setElsaClientCompanies(data);
    } catch (error) {
      if (isAxiosError(error)) {
        message.error(
          error.response?.data.message ?? t('errors.somethingWentWrong'),
        );
        close();
      }
    } finally {
      setLoadingElsaClientCompanies(false);
    }
  }, [close, clientCompany.subsidiaryCompany.id, t]);

  useEffect(() => {
    fetchElsaClientCompanies();
  }, [fetchElsaClientCompanies]);

  useEffect(() => {
    if (createNewInElsa) {
      form.setFieldValue('elsaClientCompanyId', undefined);
    }
  }, [createNewInElsa, form]);

  const existingElsaClientCompanyName = useMemo(() => {
    if (!elsaClientCompanyId) {
      return undefined;
    }

    return elsaClientCompanies.find((ecc) => ecc.id === elsaClientCompanyId)
      ?.name;
  }, [elsaClientCompanies, elsaClientCompanyId]);

  const showConnectionCard = useMemo(() => {
    return createNewInElsa || existingElsaClientCompanyName !== undefined;
  }, [createNewInElsa, existingElsaClientCompanyName]);

  const onFinish = async (values: MapCompanyModalValues) => {
    setSubmitting(true);

    try {
      let elsaClientCompanyId = values.elsaClientCompanyId;

      if (!elsaClientCompanyId) {
        const { data: newElsaCompany } = await ElsaAPI.createElsaClientCompany({
          subsidiaryCompanyId: clientCompany.subsidiaryCompany.id,
          name: clientCompany.name,
        });
        elsaClientCompanyId = newElsaCompany.id;
      }

      const { data } = await ClientCompanyAPI.updateClientCompany(
        clientCompany.id,
        {
          name: clientCompany.name,
          elsaClientCompanyId: elsaClientCompanyId,
        },
      );

      message.success(t('components.MapCompanyModal.onCreated'));
      form.resetFields();
      onUpdate?.(data);
      close();
    } catch (error) {
      if (isAxiosError(error) && !error.response?.data.message) {
        message.error(t('errors.somethingWentWrong'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        key="course-modal"
        width={600}
        title={
          <span>
            {t('components.MapCompanyModal.title')}{' '}
            <Tooltip
              title={t('components.MapCompanyModal.info')}
              placement="right">
              <QuestionCircleOutlined />
            </Tooltip>
          </span>
        }
        open
        onCancel={close}
        footer={
          <div className="flex justify-end gap-2">
            <Button key="cancel" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button
              key="save"
              type="primary"
              loading={submitting}
              disabled={!createNewInElsa && !elsaClientCompanyId}
              form="form"
              htmlType="submit">
              {createNewInElsa
                ? t('components.MapCompanyModal.addCustomer')
                : t('components.MapCompanyModal.connectToCustomer')}
            </Button>
          </div>
        }
        maskClosable={false}>
        <Form
          id="form"
          form={form}
          onFinish={(values) => onFinish(values as MapCompanyModalValues)}
          initialValues={{
            elsaClientCompanyId: undefined,
          }}>
          <div
            className={twMerge(
              'mb-1 gap-2 text-md',
              createNewInElsa && 'text-gray-500',
            )}>
            {t('components.MapCompanyModal.findInElsa')}
          </div>

          <FieldSelect
            disabled={createNewInElsa}
            formName="elsaClientCompanyId"
            formRules={[]}
            elements={elsaClientCompanies.map((ecc) => ({
              primaryText: ecc.name,
              id: ecc.id,
            }))}
            loadingOptions={loadingElsaClientCompanies}
            placeholderText={t('components.MapCompanyModal.selectElsaCompany')}
          />

          <Checkbox
            className="my-4"
            defaultChecked={false}
            onChange={(event) => setCreateNewInElsa(event.target.checked)}>
            {t('components.MapCompanyModal.createNewInElsa')}
          </Checkbox>
        </Form>

        {showConnectionCard && (
          <div className="mb-4 px-4 pb-4 pt-2 bg-blue-50 border border-blue-200 rounded flex flex-col w-full">
            <div className="font-bold mb-6">
              {t('components.MapCompanyModal.connectCompany')}
            </div>
            <div className="flex flex-row items-center gap-6">
              <div className="max-w-[50%]">
                <div className="font-bold">
                  {t('components.MapCompanyModal.herman')}
                </div>
                <div className="break-all">{clientCompany.name}</div>
              </div>
              <div>
                <ArrowRightOutlined />
              </div>
              <div className="max-w-[50%]">
                <div className="font-bold">
                  {t('components.MapCompanyModal.elsa')}
                </div>
                <div className="flex flex-row">
                  <div className="break-all">
                    {createNewInElsa ? (
                      <>
                        {clientCompany.name}
                        <b className="ml-1">
                          ({t('components.MapCompanyModal.new')})
                        </b>
                      </>
                    ) : (
                      <>{existingElsaClientCompanyName}</>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};
